'use client';
import {VillageFutureBooking, VillagePriceVariants} from '@/services/types';
import useVillage from './useVillage';
import moment from 'moment';

type SelectedDates = {
  start: Date | null;
  end: Date | null;
};

export type CalendarDateInfos = {
  canCheckIn: boolean;
  enabledCheckInDays: string[];
  isOptioned: boolean;
  isBusy: boolean;
  isBeforeInvalidPeriod: boolean;
  isAfterInvalidPeriod: boolean;
  isBeforeOptionedDate: boolean;
  isAfterOptionedDate: boolean;
};

export default function useVillageDates() {
  const {
    getPriceVariant,
    getPricePeriod,
    getUnavailableDates,
    checkIfCanCheckIn,
  } = useVillage({});

  function getDateInfos({
    date,
    futureBookings,
    priceVariants,
  }: {
    date: Date;
    futureBookings: VillageFutureBooking[];
    priceVariants: VillagePriceVariants[];
  }) {
    const dateTime = date.getTime();

    const {busy, optioned} = getUnavailableDates(futureBookings);

    const {canCheckIn, checkInDays} = checkIfCanCheckIn(date, priceVariants);

    const isOptioned = optioned.some(({timestampStart, timestampEnd}) => {
      const startTime = timestampStart.valueOf();
      const endTime = timestampEnd.valueOf();

      return dateTime >= startTime && dateTime <= endTime;
    });

    const isBusy = busy.some(({timestampStart, timestampEnd}) => {
      const startTime = timestampStart.valueOf();
      const endTime = timestampEnd.valueOf();

      return dateTime >= startTime && dateTime <= endTime;
    });

    const isBeforeOptionedDate = optioned.some(({timestampStart}) => {
      const dayBeforeFirstOptionedDay = moment(timestampStart)
        .subtract(1, 'day')
        .valueOf();

      return (
        dateTime >= dayBeforeFirstOptionedDay &&
        dateTime < timestampStart.valueOf()
      );
    });

    const isBeforeInvalidPeriod = busy.some(({timestampStart}) => {
      const dayBeforeFirstUnavailableDay = moment(timestampStart)
        .subtract(1, 'day')
        .valueOf();

      return (
        dateTime >= dayBeforeFirstUnavailableDay &&
        dateTime < timestampStart.valueOf()
      );
    });

    const isAfterOptionedDate = optioned.some(({timestampEnd}) => {
      const dayAfterLastOptionedDay = moment(timestampEnd)
        .add(1, 'day')
        .valueOf();

      return (
        dateTime <= dayAfterLastOptionedDay && dateTime > timestampEnd.valueOf()
      );
    });

    const isAfterInvalidPeriod = busy.some(({timestampEnd}) => {
      const dayAfterLastUnavailableDay = moment(timestampEnd)
        .add(1, 'day')
        .valueOf();

      return (
        dateTime <= dayAfterLastUnavailableDay &&
        dateTime > timestampEnd.valueOf()
      );
    });

    return {
      canCheckIn,
      enabledCheckInDays: checkInDays,
      isOptioned,
      isBusy,
      isBeforeInvalidPeriod,
      isAfterInvalidPeriod,
      isBeforeOptionedDate,
      isAfterOptionedDate,
    };
  }

  function getPriceVariantAndPeriod({
    priceVariants,
    selectedDates,
    totalBedrooms,
    totalGuests,
  }: {
    priceVariants: VillagePriceVariants[];
    selectedDates: SelectedDates;
    totalBedrooms: number;
    totalGuests: number;
  }) {
    const priceVariant = getPriceVariant({
      priceVariants,
      totalBedrooms,
      totalGuests,
    });

    if (priceVariant) {
      const pricePeriod = getPricePeriod({
        priceVariant,
        selectedDates,
      });

      if (!pricePeriod) {
        const nextYearPriceVariant = getPriceVariant({
          priceVariants,
          totalBedrooms,
          totalGuests,
          nextYear: true,
        });

        if (nextYearPriceVariant) {
          const pricePeriod = getPricePeriod({
            priceVariant: nextYearPriceVariant,
            selectedDates,
          });
          return {
            priceVariant: nextYearPriceVariant,
            pricePeriod,
          };
        }
      }

      if (pricePeriod) {
        return {
          priceVariant,
          pricePeriod,
        };
      }
    }
  }

  return {
    getPriceVariantAndPeriod,
    getDateInfos,
  };
}
